import type {ComponentType} from 'react';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {reorder} from 'shells/draggable-row/helpers';
import {BriefcaseRegularIcon} from 'shells/icon/svg/briefcase-regular-icon';
import {ChartLineUpRegularIcon} from 'shells/icon/svg/chart-line-up-regular-icon';
import {CursorRegularIcon} from 'shells/icon/svg/cursor-regular-icon';
import {GlobeAmericaRegularIcon} from 'shells/icon/svg/globe-america-regular-icon';
import {IndustryRegularIcon} from 'shells/icon/svg/industry-regular-icon';
import {PeopleRoofRegularIcon} from 'shells/icon/svg/people-roof-regular-icon';
import type {Widget as ClientWidget} from 'shells/widgets/types';

import type {EntityType, HeaderFieldFragment, HeaderFieldInput} from 'nutshell-graphql-types';
import type {
    WidgetFragmentFull as GraphQLWidget,
    WidgetInput as GraphInputWidget,
} from 'nutshell-graphql-types';
import {HeaderFieldType} from 'nutshell-graphql-types';

import type {Option} from '../../core/select/graphql-select-inline';

import type {WidgetsLocation} from './types';

const NEW_WIDGET_ID_PREFIX = 'temp';

export const mapGraphQLWidgetToClientWidget = (widget: GraphQLWidget): ClientWidget => {
    return {
        id: widget.id,
        name: widget.name,
        type: widget.type,
        isHidden: widget.isHidden,
        isRequired: widget.isRequired,
        isEditable: widget.isEditable,
        isDisabledByAdmin: widget.isDisabledByAdmin,
        customWidget:
            widget.__typename === 'CustomCustomFieldsWidget' && widget.customCustomFields
                ? {
                      fields: widget.customCustomFields.map((field) => {
                          return {
                              id: field.id,
                              name: field.attribute.name,
                              type: field.attribute.displayType,
                          };
                      }),
                  }
                : undefined,
    };
};

export const mapClientWidgetToGraphQLWidget = (widget: ClientWidget): GraphInputWidget => {
    let widgetId = widget.id;
    if (widget.type === 'CUSTOM_CUSTOM_FIELDS' && widget.id.startsWith(NEW_WIDGET_ID_PREFIX)) {
        // @ts-expect-error
        widgetId = null;
    }

    return {
        widgetId: widgetId,
        type: widget.type,
        isHidden: widget.isHidden,
        customWidgetInput:
            widget.type === 'CUSTOM_CUSTOM_FIELDS'
                ? {
                      name: widget.name,
                      fieldIds:
                          (widget.customWidget &&
                              widget.customWidget.fields.map((field) => field.id)) ||
                          [],
                  }
                : undefined,
    };
};

export const mapHeaderFieldFragmentToHeaderFieldInput = (
    headerField: HeaderFieldFragment
): HeaderFieldInput => {
    return {
        type: headerField.type,
        isEnabled: headerField.isEnabled,
    };
};

export const handleReorder = (
    startIndex: number,
    endIndex: number,
    widgets: ClientWidget[],
    setWidgets: (widgets: ClientWidget[]) => void
) => {
    setWidgets(reorder([...widgets], startIndex, endIndex));
};

export const handleHideToggle = (
    widgetId: string,
    isHidden: boolean,
    widgets: ClientWidget[],
    setWidgets: (widgets: ClientWidget[]) => void
) => {
    const updatedWidgets = widgets.map((widget) =>
        widget.id === widgetId ? {...widget, isHidden} : widget
    );

    setWidgets(updatedWidgets);
};

export const handleEditOrCreateWidget = (
    widget: ClientWidget,
    location: WidgetsLocation,
    widgets: ClientWidget[],
    setWidgets: (widgets: ClientWidget[]) => void
) => {
    /* If widget already exists, update it, otherwise add it to the end of the list */
    const existingWidgetIndex = widgets.findIndex((w) => w.id === widget.id);
    if (existingWidgetIndex !== -1) {
        const updatedWidgets = [...widgets];
        updatedWidgets[existingWidgetIndex] = widget;
        setWidgets(updatedWidgets);
    } else {
        setWidgets([...widgets, widget]);
    }
};

export const handleRemoveWidget = (
    widgetId: string,
    widgets: ClientWidget[],
    setWidgets: (widgets: ClientWidget[]) => void
) => {
    const updatedWidgets = widgets.filter((w) => w.id !== widgetId);
    setWidgets(updatedWidgets);
};

export const mapSelectOptionToWidget = (
    widgetId: string | null | undefined,
    name: string,
    customFields: Array<Option>
): ClientWidget => {
    return {
        id: widgetId ? widgetId : `${NEW_WIDGET_ID_PREFIX}-${Date.now()}-${Math.random()}`,
        name,
        type: 'CUSTOM_CUSTOM_FIELDS',
        isHidden: false,
        isRequired: true,
        isEditable: true,
        isDisabledByAdmin: false,
        customWidget: {
            fields: customFields.map((field) => ({
                id: field.id,
                name: field.name,
                type: field.type ? field.type : '',
            })),
        },
    };
};

export const getLayoutCustomizerPath = (entityType: EntityType, id?: string) => {
    switch (entityType) {
        case 'LEAD':
            return `/sales/pipelines/${id}/customize-layout`;
        case 'CONTACT':
            return `/person/customize-layout`;
        case 'ACCOUNT':
            return `/company/customize-layout`;
    }
};

export const getLayoutCustomizerViewedSetting = (entityType: EntityType) => {
    switch (entityType) {
        case 'LEAD':
            return Nut_Settings_User.SETTING_HAS_VIEWED_LEAD_CUSTOMIZE_PAGE;
        case 'CONTACT':
            return Nut_Settings_User.SETTING_HAS_VIEWED_CONTACT_CUSTOMIZE_PAGE;
        case 'ACCOUNT':
            return Nut_Settings_User.SETTING_HAS_VIEWED_ACCOUNT_CUSTOMIZE_PAGE;
    }
};

export const handleToggleHeaderField = (
    headerFieldName: string,
    isEnabled: boolean,
    headerFields: HeaderFieldFragment[],
    setHeaderFields: (headerFields: HeaderFieldFragment[]) => void
) => {
    const updatedHeaderFields = headerFields.map((field) =>
        field.type === headerFieldName ? {...field, isEnabled} : field
    );
    setHeaderFields(updatedHeaderFields);
};

export const getHeaderFieldIcon = (headerFieldName: HeaderFieldType): ComponentType<any> => {
    switch (headerFieldName) {
        case HeaderFieldType.LAST_CONTACTED:
            return CursorRegularIcon;
        case HeaderFieldType.TERRITORY:
            return GlobeAmericaRegularIcon;
        case HeaderFieldType.COMPANY_TYPE:
            return BriefcaseRegularIcon;
        case HeaderFieldType.INDUSTRY:
            return IndustryRegularIcon;
        case HeaderFieldType.NUMBER_OF_EMPLOYEES:
            return PeopleRoofRegularIcon;
        case HeaderFieldType.REVENUE:
            return ChartLineUpRegularIcon;
        default:
            return CursorRegularIcon;
    }
};
